import { useSetAddressError } from 'feature/addresses/hooks/useSetAddressError'
import { selectAddress } from 'feature/addresses/redux/addressCreateSelector'
import { AddressErrorsList } from 'feature/addresses/types'
import { useIsAuth } from 'feature/auth/hooks'
import { ScreenEnum, useScreen } from 'feature/screenMode'
import { useGetUserQuery } from 'feature/user/redux/user.api'
import { useEffect, useState } from 'react'
import { useActions, useAppSelector } from 'store/hooks'

import { orderFormSpace } from '../namespaces/orderFormSpace'
import { useLazyCheckTerritoryQuery } from '../redux/orders.api'
import { StepComponentProps } from '../types/StepComponentProps'

export function useOrderStepAddress({
  completeStep,
  handlePrevious,
}: StepComponentProps) {
  const [checkTerritory] = useLazyCheckTerritoryQuery()
  const [isButtonLoading, setButtonLoading] = useState(false)
  const [isPopupView, setPopupView] = useState(false)

  const { inputSuggest, isValid } = useAppSelector(selectAddress)
  const currentEditForm = useAppSelector(
    state => state.orderForm.currentEditForm,
  )
  const address = useAppSelector(
    state => state.orderForm.forms[currentEditForm].address,
  )

  const isAuth = useIsAuth()
  const { data: userData, isFetching: isAddressLoading } = useGetUserQuery(
    undefined,
    { skip: !isAuth || !!address },
  )
  const timeSlotsRegionFiasId = useAppSelector(
    state => state.orderForm.forms[currentEditForm].timeSlotsRegionFiasId,
  )

  const setAddressError = useSetAddressError()
  const screen = useScreen()
  const {
    setAddressOrder,
    setInputAddress,
    setClickMarkCoords,
    setCenter,
    setGeolocationMarkCoords,
    setInputAddressSuggest,
    clearInputAddress,
    setIsError,
    clearTimeSlotOrder,
    setTimeSlotsRegionFiasId,
  } = useActions()

  // ставим адрес если был при монтировании
  useEffect(() => {
    if (address) {
      setInputAddress(address.address)
      setInputAddressSuggest(address)
      setIsError(false)
      setClickMarkCoords([Number(address.geoLat), Number(address.geoLon)])
      setCenter([Number(address.geoLat), Number(address.geoLon)])
    } else {
      clearInputAddress()
      setClickMarkCoords(null)
      setGeolocationMarkCoords(null)
    }
  }, [])

  // если адрес не сохранён в сессии — подставляем адрес из профиля пользователя
  useEffect(() => {
    const customerAddress = userData?.customerAddress
    if (!customerAddress?.address || address) return
    setInputAddress(customerAddress.address)
    setInputAddressSuggest(customerAddress)
    setIsError(false)
    setClickMarkCoords([
      Number(customerAddress.geoLat),
      Number(customerAddress.geoLon),
    ])
    setCenter([Number(customerAddress.geoLat), Number(customerAddress.geoLon)])
  }, [userData])

  useEffect(() => {
    setAddressOrder(inputSuggest)
  }, [inputSuggest])

  const handlePreviousStep = () => handlePrevious(false)

  const openCityPopupHandler = () => {
    if (screen !== ScreenEnum.desktop) {
      setPopupView(true)
    }
  }

  const handleCompleteStep = () => {
    setButtonLoading(true)
    if (
      timeSlotsRegionFiasId &&
      timeSlotsRegionFiasId !== inputSuggest?.regionFiasId
    ) {
      clearTimeSlotOrder()
    }

    checkTerritory({
      regionKladrId: inputSuggest?.regionKladrId,
      cityKladrId: inputSuggest?.cityKladrId,
      areaKladrId: inputSuggest?.areaKladrId,
    }).then(res => {
      setButtonLoading(false)
      if (res.data?.addressKladrInServedTerritory) {
        completeStep(false)
        setTimeSlotsRegionFiasId(inputSuggest?.regionFiasId as string)
      } else {
        setAddressError(AddressErrorsList.notServedTerritory)
      }
    })
  }

  const getDescription = () => {
    switch (currentEditForm) {
      case orderFormSpace.FormType.new:
      case orderFormSpace.FormType.newCompany:
        return `Введите адрес, по которому планируете установку изделия. 
        Можете ввести вручную или отметить место на карте.`
      case orderFormSpace.FormType.service:
        return `Введите адрес объекта, где установлено изделие. 
        Можете ввести вручную или отметить место на карте.`
      default:
        return ''
    }
  }

  return {
    isButtonLoading,
    isAddressLoading,
    isPopupView,
    isValid,
    setPopupView,
    handlePreviousStep,
    openCityPopupHandler,
    handleCompleteStep,
    getDescription,
  }
}
